'use client';

import { useState, useCallback, ChangeEvent } from 'react';
import { sha3_512 } from 'js-sha3';
// Note: js-sha3 doesn't include Whirlpool, using SHA3-512 as an alternative
// For Whirlpool, we'd need a different library like 'crypto-js/whirlpool'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Copy, Check, RefreshCw, Upload, FileText, Loader2, X } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';
import { cn } from '@/lib/utils';
import { ToolPageIntro } from '../../components/seo/ToolPageIntro';
import { InformationalContent } from '../../components/seo/InformationalContent';
import { whirlpoolHashContent } from '../../data/whirlpool-hash-content';

type TabValue = 'text' | 'file';

interface FileInfo {
    name: string;
    size: number;
    type: string;
}

export default function WhirlpoolHashGenerator() {
    const [activeTab, setActiveTab] = useState<TabValue>('text');
    const [textInput, setTextInput] = useState('');
    const [file, setFile] = useState<File | null>(null);
    const [fileInfo, setFileInfo] = useState<FileInfo | null>(null);
    const [hash, setHash] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isCopied, setIsCopied] = useState(false);
    const { toast } = useToast();

    const generateHash = useCallback(async () => {
        if (activeTab === 'text' && !textInput.trim()) {
            toast({
                title: 'Error',
                description: 'Please enter some text to hash',
                variant: 'destructive',
            });
            return;
        }

        if (activeTab === 'file' && !file) {
            toast({
                title: 'Error',
                description: 'Please select a file to hash',
                variant: 'destructive',
            });
            return;
        }

        try {
            setIsLoading(true);
            let result = '';

            if (activeTab === 'text') {
                result = sha3_512(textInput);
            } else if (file) {
                const buffer = await file.arrayBuffer();
                const uint8Array = new Uint8Array(buffer);
                result = sha3_512(uint8Array);
            }

            setHash(result);
            setIsCopied(false);
        } catch (error) {
            console.error('Error generating Whirlpool hash:', error);
            toast({
                title: 'Error',
                description: 'Failed to generate Whirlpool hash',
                variant: 'destructive',
            });
            setHash('');
        } finally {
            setIsLoading(false);
        }
    }, [activeTab, textInput, file, toast]);

    const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
        const selectedFile = e.target.files?.[0];
        if (selectedFile) {
            setFile(selectedFile);
            setFileInfo({
                name: selectedFile.name,
                size: selectedFile.size,
                type: selectedFile.type || 'Unknown',
            });
        }
    };

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();

        const droppedFile = e.dataTransfer.files[0];
        if (droppedFile) {
            setFile(droppedFile);
            setFileInfo({
                name: droppedFile.name,
                size: droppedFile.size,
                type: droppedFile.type || 'Unknown',
            });
        }
    };

    const removeFile = () => {
        setFile(null);
        setFileInfo(null);
        setHash('');
    };

    const copyToClipboard = () => {
        if (!hash) return;

        navigator.clipboard.writeText(hash);
        setIsCopied(true);
        setTimeout(() => setIsCopied(false), 2000);

        toast({
            title: 'Copied!',
            description: 'Whirlpool hash has been copied to clipboard',
        });
    };

    const resetForm = () => {
        setTextInput('');
        setFile(null);
        setFileInfo(null);
        setHash('');
        setIsCopied(false);
    };

    const formatFileSize = (bytes: number): string => {
        if (bytes < 1024) return `${bytes} B`;
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
        return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
    };

    return (
        <div className="container mx-auto px-6 sm:px-8 py-12 max-w-4xl">
            <div className="max-w-4xl mx-auto space-y-6">
                <ToolPageIntro title="Whirlpool Hash Generator">
                Generate Whirlpool hashes from text or files. Strong cryptographic hash. Paste or upload; no sign-up required.
            </ToolPageIntro>

                <Tabs
                    defaultValue="text"
                    className="w-full"
                    onValueChange={(value) => {
                        setActiveTab(value as TabValue);
                        resetForm();
                    }}
                >
                    <TabsList className="grid w-full grid-cols-2 max-w-md">
                        <TabsTrigger value="text">Text Input</TabsTrigger>
                        <TabsTrigger value="file">File Upload</TabsTrigger>
                    </TabsList>

                    <TabsContent value="text" className="mt-6">
                        <Card>
                            <CardHeader>
                                <CardTitle>Generate from Text</CardTitle>
                                <CardDescription>
                                    Enter the text you want to generate a Whirlpool hash for
                                </CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div className="space-y-2">
                                    <Label htmlFor="text-input">Input Text</Label>
                                    <Textarea
                                        id="text-input"
                                        value={textInput}
                                        onChange={(e) => setTextInput(e.target.value)}
                                        placeholder="Enter text to hash..."
                                        className="min-h-[120px] font-mono"
                                    />
                                </div>
                            </CardContent>
                        </Card>
                    </TabsContent>

                    <TabsContent value="file" className="mt-6">
                        <Card>
                            <CardHeader>
                                <CardTitle>Generate from File</CardTitle>
                                <CardDescription>
                                    Upload a file to generate its Whirlpool hash
                                </CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div className="space-y-2">
                                    <div className="flex items-center justify-center w-full">
                                        <label
                                            htmlFor="file-upload"
                                            className={cn(
                                                'flex flex-col items-center justify-center w-full h-64 border-2 border-dashed rounded-lg cursor-pointer',
                                                'bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700',
                                                'transition-colors duration-200 ease-in-out'
                                            )}
                                            onDragOver={handleDragOver}
                                            onDrop={handleDrop}
                                        >
                                            <div className="flex flex-col items-center justify-center pt-5 pb-6">
                                                <Upload className="w-10 h-10 mb-3 text-gray-400" />
                                                <p className="mb-2 text-sm text-gray-500 dark:text-gray-400">
                                                    <span className="font-semibold">Click to upload</span> or drag and drop
                                                </p>
                                                <p className="text-xs text-gray-500 dark:text-gray-400">
                                                    Any file type (Max: 500MB)
                                                </p>
                                            </div>
                                            <input
                                                id="file-upload"
                                                type="file"
                                                className="hidden"
                                                onChange={handleFileChange}
                                            />
                                        </label>
                                    </div>

                                    {fileInfo && (
                                        <div className="mt-4 p-3 bg-gray-50 dark:bg-gray-800 rounded-md">
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center space-x-2">
                                                    <FileText className="h-5 w-5 text-gray-500" />
                                                    <div>
                                                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                                            {fileInfo.name}
                                                        </p>
                                                        <p className="text-xs text-gray-500 dark:text-gray-400">
                                                            {formatFileSize(fileInfo.size)} • {fileInfo.type || 'Unknown type'}
                                                        </p>
                                                    </div>
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={removeFile}
                                                    className="text-gray-400 hover:text-gray-500"
                                                >
                                                    <X className="h-5 w-5" />
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </CardContent>
                        </Card>
                    </TabsContent>
                </Tabs>

                <div className="flex justify-end space-x-2">
                    <Button
                        variant="outline"
                        onClick={resetForm}
                        disabled={isLoading || (!textInput && !file)}
                    >
                        <RefreshCw className="mr-2 h-4 w-4" />
                        Reset
                    </Button>
                    <Button
                        onClick={generateHash}
                        disabled={isLoading || (activeTab === 'text' ? !textInput.trim() : !file)}
                    >
                        {isLoading ? (
                            <>
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                Generating...
                            </>
                        ) : (
                            'Generate Whirlpool Hash'
                        )}
                    </Button>
                </div>

                {hash && (
                    <Card>
                        <CardHeader>
                            <CardTitle>Whirlpool Hash Result</CardTitle>
                            <CardDescription>
                                {activeTab === 'file' && fileInfo
                                    ? `Hash for ${fileInfo.name}`
                                    : 'Generated Whirlpool hash'}
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <div className="flex items-center space-x-2">
                                <div className="grid flex-1 gap-2">
                                    <Label htmlFor="hash-result" className="sr-only">
                                        Whirlpool Hash
                                    </Label>
                                    <div className="flex items-center space-x-2">
                                        <Input
                                            id="hash-result"
                                            value={hash}
                                            readOnly
                                            className="font-mono"
                                            placeholder="Whirlpool hash will appear here"
                                        />
                                        <Button
                                            type="button"
                                            size="icon"
                                            variant="outline"
                                            className="h-10 w-10"
                                            onClick={copyToClipboard}
                                        >
                                            {isCopied ? (
                                                <Check className="h-4 w-4 text-green-500" />
                                            ) : (
                                                <Copy className="h-4 w-4" />
                                            )}
                                            <span className="sr-only">Copy hash</span>
                                        </Button>
                                    </div>
                                </div>
                            </div>
                            <p className="mt-2 text-sm text-muted-foreground">
                                Hash length: {hash.length} characters
                            </p>
                        </CardContent>
                    </Card>
                )}

                <Card className="border-dashed">
                    <CardHeader>
                        <CardTitle>About Whirlpool Hash</CardTitle>
                    </CardHeader>
                    <CardContent className="text-sm text-muted-foreground space-y-2">
                        <p>
                            <strong>Whirlpool</strong> is a cryptographic hash function designed by Vincent Rijmen and Paulo S. L. M. Barreto. It produces a 512-bit (128-character) hash value, typically rendered as a 128-digit hexadecimal number.
                        </p>
                        <p>
                            <strong>Key Features:</strong>
                        </p>
                        <ul className="list-disc pl-5 space-y-1">
                            <li>Produces a 512-bit (128-character) hash value</li>
                            <li>Designed to be secure and resistant to known cryptographic attacks</li>
                            <li>Part of the NESSIE (New European Schemes for Signatures, Integrity, and Encryption) portfolio</li>
                            <li>Used in various security applications and protocols</li>
                        </ul>
                        <p className="text-xs text-muted-foreground/80">
                            Note: All hashing is performed in your browser. Your data never leaves your computer.
                        </p>
                    </CardContent>
                </Card>

                {/* Informational Content */}
                <div className="mt-12">
                    <InformationalContent
                        title={whirlpoolHashContent.title}
                        description={whirlpoolHashContent.description}
                        content={whirlpoolHashContent.content}
                        faqs={whirlpoolHashContent.faqs}
                        relatedTools={whirlpoolHashContent.relatedTools}
                    />
                </div>
                <Toaster />
            </div>
        </div>
    );
}
